<script>
	//@ts-nocheck
	import HorizontalBarchart from './HorizontalBarchart.svelte';
	import { format } from 'd3-format';
	import { getPartyColor } from '$lib/utils/colors';

	export let data;
	export let isSelected = false;
	export let isHovered = false;
	export let onClick = () => {};

	function independentLookup(partyAcronym) {
		const independentSymbols = [
			'ARNAB',
			'BAJU',
			'BUKU',
			'CEREK',
			'CINCIN',
			'GAJAH',
			'JAM',
			'KERUSI',
			'KUDA',
			'KUNCI',
			'MANGGIS',
			'PEN',
			'POKOK',
			'RUMAH',
			'TRAKTOR',
			'UDANG'
		];
		
		if (partyAcronym && independentSymbols.includes(partyAcronym.toUpperCase())) {
			return 'BEBAS';
		}
		
		return partyAcronym;
	}

	// Helper function to get party logo path
	function getPartyLogoPath(partyAcronym) {
		if (!partyAcronym || partyAcronym === '') return null;
		

		// console.log('partyAcronym', partyAcronym);
		// List of available logo files
		const availableLogos = new Set([
			'PH.PNG', 'PBM.PNG', 'BN.PNG', 'Aspirasi.PNG', 'Bersama.PNG', 
			'KDM.PNG', 'PN.PNG', 'Independent.PNG', 'Rumpun.PNG', 'GAS.PNG', 
			'PR.PNG', 'GRS.PNG', 'PIS.PNG', 'PPRS.PNG', 'SPP.PNG', 
			'Warisan.PNG', 'PBK.PNG', 'Perpaduan.PNG', 'PKS.PNG', 
			'Anak_Negeri.PNG', 'SAPP.PNG', 'STARSABAH.PNG', 'UPKO.PNG'
		]);
		
		// Mapping for special cases
		const logoMap = {
			'ANAK NEGERI': 'Anak_Negeri.PNG',
			'WARISAN': 'Warisan.PNG',
			'RUMPUN': 'Rumpun.PNG',
			'ASPIRASI': 'Aspirasi.PNG',
			'PERPADUAN': 'Perpaduan.PNG',
			'STARSABAH': 'STARSABAH.PNG',
			'INDEPENDENT': 'Independent.PNG',
			'BERSAMA': 'Bersama.PNG'
		};
		
		let logoFilename;
		
		// Check if there's a direct mapping
		if (logoMap[partyAcronym]) {
			logoFilename = logoMap[partyAcronym];
		} else if (partyAcronym.toUpperCase().includes('BEBAS') || independentLookup(partyAcronym) === 'BEBAS') {
			logoFilename = 'Independent.PNG';
		} else {
			// For other acronyms, use uppercase directly
			logoFilename = `${partyAcronym.toUpperCase()}.PNG`;
		}
		
		// Only return path if logo exists
		if (availableLogos.has(logoFilename)) {
			return `/party_logos/${logoFilename}`;
		}
		
		return null;
	}

	$: prepBarChartData = () => {
		if (!data?.result) return [];
		
		return data.result
			.filter((result) => {
				// Filter out entries with empty or invalid votes_share
				const votesShare = result.votes_share;
				return votesShare !== '' && votesShare !== null && votesShare !== undefined && !isNaN(Number(votesShare));
			})
			.map((result) => ({
				category: result.party_name_pretty_en || 'Unknown',
				value: Number(result.votes_share) || 0,
				color: getColor(result, true).fill
			}))
			.sort((a, b) => b.value - a.value);
	};

	$: getColor = (result, justParty = false) => {
		// Fallback colors for edge cases
		const fallbackColors = {
			"map-outline": "#000000",
			"Independent": "#DCDCDC"
		};

		if (justParty) {
			// Use getPartyColor with the result object (which has party_acronym_en)
			if (result && typeof result === 'object' && result.party_acronym_en) {
				return getPartyColor(result, true);
			}
			// Fallback for string or invalid result
			if (typeof result === 'string') {
				return getPartyColor(result, true);
			}
			return {
				fill: fallbackColors['map-outline']
			};
		}
		
		// Handle case where result is an array (finding winner)
		if (Array.isArray(result)) {
			let winner = result.find((r) => r.result === 'winner');
			if (winner && winner.party_acronym_en) {
				return getPartyColor(winner, false);
			}
			return {
				fill: fallbackColors['map-outline']
			};
		}
		
		// Default fallback
		return {
			fill: fallbackColors['map-outline']
		};
	};

	// $: console.log('data', data)

	let cardWidth = 0;

	// Calculate chart width: ResizeObserver's contentRect.width already excludes padding
	$: chartWidth = cardWidth > 0 ? Math.max(cardWidth, 250) : (typeof window !== 'undefined' && window.innerWidth <= 767 ? 300 : 380);

	function resizeObserver(node) {
		const resizeObserver = new ResizeObserver((entries) => {
			for (const entry of entries) {
				cardWidth = entry.contentRect.width;
			}
		});
		resizeObserver.observe(node);
		return {
			destroy() {
				resizeObserver.disconnect();
			}
		};
	}
</script>

<div 
	use:resizeObserver
	class="election-card p-4 bg-white border rounded-lg shadow-sm cursor-pointer transition-all duration-200 hover:shadow-md w-full max-w-full {
		isSelected ? 'border-2 border-blue-500 shadow-lg' : 
		isHovered ? 'border-2 border-amber-500 shadow-md' : 
		'border border-gray-200'
	}"
	on:click={onClick}
	role="button"
	tabindex="0"
	on:keydown={(e) => e.key === 'Enter' && onClick()}
>
	<div class="mb-3 card-header">
		<div class="text-lg font-bold text-gray-800">{data?.result?.[0]?.seat_name_en || 'Unknown District'}</div>
		<div class="text-sm text-gray-600">{data?.result?.[0]?.pcode.split('_')?.[1] || ''}</div>
	</div>
	
	<!-- Horizontal Bar Chart for Vote Percentages -->
	<div class="mb-3 card-chart">
		<HorizontalBarchart 
			data={prepBarChartData()} 
			width={chartWidth} 
			labelSizeEm={0.6}
			getValueForDisplay={(d) => d}
			formatNumber={(d) => format('.0f')(d*100) + '%'}
		/>
	</div>
	
	<!-- Party Results -->
	<div class="card-results">
		<!-- Column Headers -->
		<div class="pb-1 mb-2 border-b border-gray-200">
			<div class="flex items-center">
				<div class="flex-shrink-0 mr-2 w-8"></div>
				<div class="flex-1 text-xs font-semibold text-gray-600 uppercase">Party/Candidate</div>
				<div class="w-24 text-xs font-semibold text-right text-gray-600 uppercase">Vote Count</div>
				<div class="mr-3 w-20 text-xs font-semibold text-right text-gray-600 uppercase">Vote Share</div>
			</div>
		</div>
		
		{#each (data?.result?.filter((d) => d.result && d.result !== '' && d.result === 'winner') || []).sort((a, b) => (b.votes || 0) - (a.votes || 0)) as result}
			<div class="mb-1">
				<div class="flex items-center">
					<div class="flex flex-row flex-shrink-0 gap-1.5 items-center mr-2">
						<div
							class="w-3 h-3 rounded-full"
							style="background-color: {getColor(result, true).fill};"
						></div>
						<div class="w-6 h-6">
							{#if getPartyLogoPath(result.party_acronym_en)}
								<img 
									src={getPartyLogoPath(result.party_acronym_en)} 
									alt={result.party_name_pretty_en || ''}
									class="object-contain w-6 h-6"
								/>
							{/if}
						</div>
					</div>
					<div class="flex flex-1 gap-1.5 items-center text-sm font-bold text-gray-700">
						<span>{result.party_name_pretty_en}:</span>
						<img src="/check-circle.svg" alt="Winner" class="flex-shrink-0 w-6 h-6" />
					</div>
					<div class="w-24 font-mono text-sm text-right text-gray-500">{result.votes ? format(',')(result.votes) : '-'}</div>
					<div class="mr-3 w-20 font-mono text-sm text-right text-gray-600">{result.votes_share && result.votes_share !== '' && !isNaN(Number(result.votes_share)) ? format('.1f')(Number(result.votes_share) * 100) + '%' : '-'}</div>
				</div>
				{#if result.candidate_name}
					<div class="flex items-start">
						<div class="flex flex-row flex-shrink-0 gap-1.5 items-center mr-2">
							<div class="w-3 h-3"></div>
							<div class="w-6 h-6"></div>
						</div>
						<div class="flex-1 text-xs text-gray-500 break-words">{result.candidate_name}</div>
						<div class="mr-3 w-20"></div>
						<div class="w-24"></div>
					</div>
				{/if}
			</div>
		{/each}

		{#each (data?.result?.filter((d) => d.result && d.result !== '' && d.result === 'runnerup') || []).sort((a, b) => (b.votes || 0) - (a.votes || 0)) as result}
			<div class="mb-1">
				<div class="flex items-center">
					<div class="flex flex-row flex-shrink-0 gap-1.5 items-center mr-2">
						<div
							class="w-3 h-3 rounded-full"
							style="background-color: {getColor(result, true).fill};"
						></div>
						<div class="w-6 h-6">
							{#if getPartyLogoPath(result.party_acronym_en)}
								<img 
									src={getPartyLogoPath(result.party_acronym_en)} 
									alt={result.party_name_pretty_en || ''}
									class="object-contain w-6 h-6"
								/>
							{/if}
						</div>
					</div>
					<div class="flex-1 text-sm font-bold text-gray-700">{result.party_name_pretty_en}:</div>
					<div class="w-24 font-mono text-sm text-right text-gray-500">{result.votes ? format(',')(result.votes) : '-'}</div>
					<div class="mr-3 w-20 font-mono text-sm text-right text-gray-600">{result.votes_share && result.votes_share !== '' && !isNaN(Number(result.votes_share)) ? format('.1f')(Number(result.votes_share) * 100) + '%' : '-'}</div>
				</div>
				{#if result.candidate_name}
					<div class="flex items-start">
						<div class="flex flex-row flex-shrink-0 gap-1.5 items-center mr-2">
							<div class="w-3 h-3"></div>
							<div class="w-6 h-6"></div>
						</div>
						<div class="flex-1 text-xs text-gray-500 break-words">{result.candidate_name}</div>
						<div class="mr-3 w-20"></div>
						<div class="w-24"></div>
					</div>
				{/if}
			</div>
		{/each}

		{#each (data?.result?.filter((d) => d.result && d.result !== '' && d.result === 'other') || []).sort((a, b) => (b.votes || 0) - (a.votes || 0)) as result}
			<div class="mb-1">
				<div class="flex items-center">
					<div class="flex flex-row flex-shrink-0 gap-1.5 items-center mr-2">
						<div
							class="w-3 h-3 rounded-full"
							style="background-color: {getColor(result, true).fill};"
						></div>
						<div class="w-6 h-6">
							{#if getPartyLogoPath(result.party_acronym_en)}
								<img 
									src={getPartyLogoPath(result.party_acronym_en)} 
									alt={result.party_name_pretty_en || ''}
									class="object-contain w-6 h-6"
								/>
							{/if}
						</div>
					</div>
					<div class="flex-1 text-sm font-bold text-gray-700">{result.party_name_pretty_en}:</div>
					<div class="w-24 font-mono text-sm text-right text-gray-500">{result.votes ? format(',')(result.votes) : '-'}</div>
					<div class="mr-3 w-20 font-mono text-sm text-right text-gray-600">{result.votes_share && result.votes_share !== '' && !isNaN(Number(result.votes_share)) ? format('.1f')(Number(result.votes_share) * 100) + '%' : '-'}</div>
				</div>
				{#if result.candidate_name}
					<div class="flex items-start">
						<div class="flex flex-row flex-shrink-0 gap-1.5 items-center mr-2">
							<div class="w-3 h-3"></div>
							<div class="w-6 h-6"></div>
						</div>
						<div class="flex-1 text-xs text-gray-500 break-words">{result.candidate_name}</div>
						<div class="mr-3 w-20"></div>
						<div class="w-24"></div>
					</div>
				{/if}
			</div>
		{/each}
	</div>
</div>

<style>
	.election-card {
		width: 100%;
		max-width: 100%;
		box-sizing: border-box;
	}
	
	.election-card:focus {
		outline: 2px solid #3b82f6;
		outline-offset: 2px;
	}
	
	.card-header {
		width: 100%;
	}
	
	.card-chart {
		width: 100%;
		overflow: hidden;
	}
	
	.card-results {
		width: 100%;
		overflow-x: auto;
	}
	
	/* Mobile adjustments */
	@media (max-width: 767px) {
		.election-card {
			padding: 0.75rem;
			min-width: 0;
		}
		
		.card-results {
			font-size: 0.875rem;
		}
		
		.card-results :global(.text-xs) {
			font-size: 0.7rem;
		}
		
		.card-results :global(.text-sm) {
			font-size: 0.8rem;
		}
	}
</style>


